import { Container, Form, ProgressBar } from 'react-bootstrap';
import Stepper from 'react-stepper-horizontal';
import { useState, useEffect, useCallback } from 'react';

import { PageHeader } from '../components/Layout/PageHeader';
import { DataCollectionNotice } from '../components/Layout/DataCollectionNotice';
import { OperationsAndInfo } from '../components/HazMatPermit/OperationsAndInfo';
import { EmergencyContacts } from '../components/HazMatPermit/EmergencyContacts';
import { HazmatInventory } from '../components/HazMatPermit/HazmatInventory';
import { HazMatDeclaration } from '../components/HazMatPermit/HazMatDeclaration';
import { HazmatType } from '../components/HazMatPermit/HazmatType';
import { SubmitSuccess } from '../components/SubmitSuccess';
import { FloorPlan } from '../components/HazMatPermit/FloorPlan';
import { SomethingBroke } from '../components/Errors/SomethingBroke';
import FormClientError from '../components/Errors/FormClientError';
import { InternalServerError } from '../components/Errors/InternalServerError';
import { SiteMap } from '../components/HazMatPermit/SiteMap';
import dayjs from 'dayjs';

export function HazMatPermit() {
	const [step, setStep] = useState(1);
	const [somethingBroke, setSomethingBroke] = useState(false);
	const [submitDone, setSubmitDone] = useState(false);
	const [currOpsAndInfo, setCurrOpsAndInfo] = useState({
		email: '',
		title: '',
		phone: '',
	});
	const [currContacts, setCurrContacts] = useState();
	const [currInventory, setCurrInventory] = useState();
	const [currDeclaration, setCurrDeclaration] = useState({
		email: '',
		title: '',
		phone: '',
		name: '',
	});
	const [currFloorPlan, setCurrFloorPlan] = useState({
		file: null,
		answer: null,
	});
	const [currSiteMap, setCurrSiteMap] = useState({ file: null, answer: null });
	const [httpStatusCode, setHttpStatusCode] = useState(null);
	const [currHazType, setCurrHazType] = useState({
		newOrRenew: 'new',
		type: [],
	});

	const createBody = declarationData => {
		const body = new FormData();
		const data = {
			...currOpsAndInfo,
			...currHazType,
			hrsOperation: JSON.stringify(currOpsAndInfo.hrsOperation),
			inventory: JSON.stringify(currInventory.inventory),
			contacts: JSON.stringify(currContacts.contacts),
			floorPlan: JSON.stringify(currFloorPlan),
			siteMap: JSON.stringify(currSiteMap),
			date: dayjs().format('MM/DD/YYYY'),
			declaration: JSON.stringify(declarationData),
			name: declarationData.name,
			phone: declarationData.phone,
			email: declarationData.email,
			title: declarationData.title,
		};

		body.append('file', currFloorPlan.file);
		body.append('file', currSiteMap.file);
		for (var key in data) {
			body.append(key, data[key]);
		}

		return body;
	};

	const submitForm = async declarationData => {
		try {
			setSubmitDone(true);
			const { status } = await fetch(
				`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}/hazmat-permit`,
				{
					method: 'POST',
					body: createBody(declarationData),
				}
			);
			setHttpStatusCode(status);
			setSubmitDone(false);
		} catch (err) {
			console.error(err);
			setSomethingBroke(true);
		}
	};

	const incrementStep = useCallback(() => {
		setStep(curStep => curStep + 1);
	}, []);

	const decrementStep = useCallback(() => {
		setStep(curStep => curStep - 1);
	}, []);

	const handleOnNext = (setData = () => {}) => {
		return data => {
			setData(data);
			incrementStep();
		};
	};

	const handleSubmit = declarationData => {
		setCurrDeclaration(declarationData);
		setSubmitDone(true);
		submitForm(declarationData);
	};

	useEffect(() => {
		if (httpStatusCode === 200) {
			setHttpStatusCode(null);
			incrementStep();
		}
	}, [httpStatusCode, incrementStep]);

	if (somethingBroke === true) {
		return <SomethingBroke></SomethingBroke>;
	}

	if (httpStatusCode >= 500) {
		return <InternalServerError></InternalServerError>;
	}

	if (httpStatusCode >= 400) {
		return (
			<FormClientError
				returnToForm={() => {
					setStep(1);
					setHttpStatusCode(null);
				}}
			/>
		);
	}

	const steps = [
		{
			component: (
				<HazmatType
					key="hazmatType"
					currHazType={currHazType}
					onNext={handleOnNext(setCurrHazType)}
					finalStep={false}
					disablePrev={true}
					disableNext={false}
				/>
			),
			title: 'Permit Type',
		},
		{
			component: (
				<OperationsAndInfo
					key="opsAndInf"
					operInfo={currOpsAndInfo}
					onNext={handleOnNext(setCurrOpsAndInfo)}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
					onPrevious={decrementStep}
				/>
			),
			title: 'Operations and Information',
		},
		{
			component: (
				<EmergencyContacts
					key="emergencyContact"
					currContacts={currContacts}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrContacts)}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Contact Info',
		},
		{
			component: (
				<HazmatInventory
					key="hazmatInv"
					currInventory={currInventory}
					onNext={handleOnNext(setCurrInventory)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Hazardous Materials',
		},
		{
			component: (
				<FloorPlan
					key="floorPlan"
					currFloorPlan={currFloorPlan}
					onNext={handleOnNext(setCurrFloorPlan)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Floor Plan',
		},
		{
			component: (
				<SiteMap
					key="siteMap"
					currSiteMap={currSiteMap}
					onNext={handleOnNext(setCurrSiteMap)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Site Map',
		},
		{
			component: (
				<HazMatDeclaration
					key="hazMatDecl"
					currDeclaration={currDeclaration}
					onSubmit={handleSubmit}
					onPrevious={decrementStep}
					finalStep={true}
					disablePrev={false}
					disableNext={false}
					enableLoading={submitDone}
				/>
			),
			title: 'Declaration',
		},
		{
			component: <SubmitSuccess key="submitSuccess" />,
			title: 'Submission',
		},
	];

	return (
		<Container fluid className="p-0 mb-5 permit">
			<PageHeader title="HazMat Permit" />

			<Form className="app-container">
				<DataCollectionNotice />

				<div className="stepper">
					<Stepper
						steps={steps.map(({ title }, i) => ({
							title,
							onClick: () => setStep(i + 1),
						}))}
						activeStep={step - 1}
					/>
				</div>

				<div className="bar">
					<ProgressBar className="mb-4" now={(step / steps.length) * 100} />
				</div>

				{steps.map(({ component }, index) =>
					step === index + 1 ? component : ''
				)}
			</Form>
		</Container>
	);
}
